import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import PageContainer from '@/components/layout/PageContainer'
import { Card, MetricCard, StatusBadge, LoadingSpinner } from '@/components/common'
import { AIInsightPanel } from '@/components/insights'
import { ForecastChart, RiskMatrix } from '@/components/charts'
import { useUIStore, useAssetStore } from '@/store'
import {
  dashboardApi,
  FleetModelMetrics,
  UpcomingEvent,
  WhatIfScenariosData,
  WhatIfScenario
} from '@/services/dashboardApi'
import { RISK_SEVERITY_THRESHOLDS } from '@/constants/thresholds'
import { Calendar, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, Shield, Activity } from 'lucide-react'

// Types
interface ForecastData {
  historical: {
    timestamps: string[]
    values: number[]
  }
  forecast: {
    timestamps: string[]
    values: number[]
    lowerBound: number[]
    upperBound: number[]
  }
}

interface RiskDataItem {
  id: string
  label: string
  probability: number
  impact: number
}

// Default mock data
const defaultForecast: ForecastData = {
  historical: {
    timestamps: ['Day -7', 'Day -6', 'Day -5', 'Day -4', 'Day -3', 'Day -2', 'Day -1', 'Today'],
    values: [0.25, 0.28, 0.32, 0.35, 0.42, 0.48, 0.55, 0.62]
  },
  forecast: {
    timestamps: ['Today', 'Day +1', 'Day +2', 'Day +3', 'Day +4', 'Day +5', 'Day +6', 'Day +7'],
    values: [0.62, 0.68, 0.72, 0.75, 0.78, 0.80, 0.82, 0.85],
    lowerBound: [0.55, 0.58, 0.60, 0.62, 0.65, 0.68, 0.70, 0.72],
    upperBound: [0.69, 0.78, 0.84, 0.88, 0.91, 0.92, 0.94, 0.98]
  }
}

const defaultRiskData: RiskDataItem[] = [
  { id: '1', label: 'F67503', probability: 0.85, impact: 0.9 },
  { id: '2', label: 'F69871', probability: 0.75, impact: 0.8 },
  { id: '3', label: 'F68885', probability: 0.45, impact: 0.6 },
  { id: '4', label: 'F70231', probability: 0.35, impact: 0.45 },
  { id: '5', label: 'F70478', probability: 0.15, impact: 0.2 },
  { id: '6', label: 'F67548', probability: 0.1, impact: 0.15 },
]

export default function PredictiveOps() {
  const navigate = useNavigate()
  const { addNotification } = useUIStore()
  const { assets, fetchAssets } = useAssetStore()
  const [selectedTimeframe, setSelectedTimeframe] = useState('7d')

  // Dynamic data state
  const [forecastData, setForecastData] = useState<ForecastData>(defaultForecast)
  const [riskData, setRiskData] = useState<RiskDataItem[]>(defaultRiskData)
  const [loading, setLoading] = useState(true)
  const [usingMockData, setUsingMockData] = useState(false)

  // New dynamic state for API data
  const [modelMetrics, setModelMetrics] = useState<FleetModelMetrics | null>(null)
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([])
  const [whatIfScenarios, setWhatIfScenarios] = useState<WhatIfScenario[]>([])
  const [scenarioSummary, setScenarioSummary] = useState<WhatIfScenariosData['summary'] | null>(null)

  // Calculated metrics with risk distribution - considers BOTH probability AND impact
  // Combined risk score = probability × impact (both on 0-1 scale)
  const riskDistribution = useMemo(() => {
    const getCombinedRiskScore = (r: RiskDataItem) => r.probability * r.impact

    // Using combined risk thresholds from shared constants
    const critical = riskData.filter(r => {
      const score = getCombinedRiskScore(r)
      return score >= RISK_SEVERITY_THRESHOLDS.COMBINED_CRITICAL
    })
    const elevated = riskData.filter(r => {
      const score = getCombinedRiskScore(r)
      return score >= RISK_SEVERITY_THRESHOLDS.COMBINED_HIGH && score < RISK_SEVERITY_THRESHOLDS.COMBINED_CRITICAL
    })
    const moderate = riskData.filter(r => {
      const score = getCombinedRiskScore(r)
      return score >= RISK_SEVERITY_THRESHOLDS.COMBINED_MEDIUM && score < RISK_SEVERITY_THRESHOLDS.COMBINED_HIGH
    })
    const low = riskData.filter(r => {
      const score = getCombinedRiskScore(r)
      return score < RISK_SEVERITY_THRESHOLDS.COMBINED_MEDIUM
    })

    return { critical, elevated, moderate, low }
  }, [riskData])

  const assetsAtRisk = useMemo(() => riskDistribution.critical.length + riskDistribution.elevated.length, [riskDistribution])
  const predictedFailures = useMemo(() => riskDistribution.critical.length, [riskDistribution])

  // Calculate trend from forecast data
  const forecastTrend = useMemo(() => {
    if (!forecastData?.forecast?.values || forecastData.forecast.values.length < 2) return null
    const values = forecastData.forecast.values
    const startVal = values[0]
    const endVal = values[values.length - 1]
    const change = ((endVal - startVal) / startVal) * 100
    return {
      direction: change > 5 ? 'increasing' : change < -5 ? 'decreasing' : 'stable',
      percentage: Math.abs(change).toFixed(1),
      endValue: (endVal * 100).toFixed(0)
    }
  }, [forecastData])

  // Calculate potential impact if no action taken
  const potentialImpact = useMemo(() => {
    const criticalAssets = riskDistribution.critical.length
    const elevatedAssets = riskDistribution.elevated.length
    // Estimate based on typical failure costs
    const estimatedDowntimeHours = criticalAssets * 8 + elevatedAssets * 4
    const estimatedCost = criticalAssets * 15000 + elevatedAssets * 5000
    return { downtime: estimatedDowntimeHours, cost: estimatedCost }
  }, [riskDistribution])

  // Fetch predictive data from API
  const fetchPredictiveData = useCallback(async () => {
    setLoading(true)

    // Guard: ensure assets is an array
    const assetList = Array.isArray(assets) ? assets : []

    try {
      // Fetch all data in parallel
      const [
        forecastResult,
        metricsResult,
        eventsResult,
        scenariosResult
      ] = await Promise.all([
        dashboardApi.getFleetForecastWithFallback(selectedTimeframe),
        dashboardApi.getFleetModelMetricsWithFallback(),
        dashboardApi.getUpcomingEventsWithFallback(selectedTimeframe),
        dashboardApi.getWhatIfScenariosWithFallback()
      ])

      // Set forecast data - ensure we have valid data structure
      if (forecastResult.data?.historical && forecastResult.data?.forecast) {
        setForecastData(forecastResult.data)
      }

      // Set model metrics - ensure we have valid data
      if (metricsResult.data) {
        setModelMetrics(metricsResult.data)
      }

      // Set upcoming events - with null safety
      const events = eventsResult.data?.events
      setUpcomingEvents(Array.isArray(events) ? events : [])

      // Set what-if scenarios - with null safety
      const scenarios = scenariosResult.data?.scenarios
      setWhatIfScenarios(Array.isArray(scenarios) ? scenarios : [])
      setScenarioSummary(scenariosResult.data?.summary ?? null)

      // Track if using mock data - if ANY source is mock, show indicator
      const isMock = forecastResult.isMock || metricsResult.isMock || eventsResult.isMock || scenariosResult.isMock
      setUsingMockData(isMock)

      // Generate risk data from actual asset list (sorted by health score)
      // This uses real API data from the fleet endpoint
      if (assetList.length > 0) {
        // Sort by health score (lowest first = highest risk)
        const sortedAssets = [...assetList].sort((a, b) => a.health_score - b.health_score)

        const risk: RiskDataItem[] = sortedAssets.slice(0, 6).map((a, i) => ({
          id: String(i + 1),
          label: a.id,
          // Calculate probability based on health score and anomaly rate
          probability: Math.min(0.95, (100 - a.health_score) / 100 + ((a.metrics?.anomaly_rate || 0) / 100) * 0.3),
          // Impact based on status
          impact: a.status === 'critical' ? 0.9 : a.status === 'warning' ? 0.7 : 0.3
        }))
        setRiskData(risk)
      }
    } catch (error) {
      console.warn('Predictive data API failed, using mock data:', error)
      setForecastData(defaultForecast)
      setRiskData(defaultRiskData)
      setUsingMockData(true)
    } finally {
      setLoading(false)
    }
  }, [assets, selectedTimeframe])

  // Fetch data on mount - only once
  useEffect(() => {
    fetchAssets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Guard against assets not being an array
  const assetsLength = Array.isArray(assets) ? assets.length : 0

  // Track if initial fetch has been done to prevent flickering
  const [initialFetchDone, setInitialFetchDone] = useState(false)

  useEffect(() => {
    // Only fetch once when assets are ready (or after timeout if no assets)
    if (initialFetchDone) return

    const timer = setTimeout(() => {
      fetchPredictiveData()
      setInitialFetchDone(true)
    }, assetsLength > 0 ? 0 : 500)

    return () => clearTimeout(timer)
    // Only depend on assetsLength to avoid re-running when fetchPredictiveData changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetsLength])

  // Refetch when timeframe changes (after initial fetch is done)
  useEffect(() => {
    if (initialFetchDone) {
      fetchPredictiveData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeframe])

  const handleRefresh = () => {
    fetchPredictiveData()
    addNotification({
      type: 'info',
      title: 'Refreshing',
      message: 'Fetching latest prediction data...'
    })
  }

  const handleRiskItemClick = (item: { id: string; label: string; probability: number; impact: number }) => {
    addNotification({
      type: 'info',
      title: 'Loading Asset Details',
      message: `Opening analysis for ${item.label}...`
    })
    navigate(`/assets/${item.label}`)
  }

  const handleEventClick = (event: { event: string; date: string; type: string }) => {
    if (event.type === 'critical') {
      addNotification({
        type: 'warning',
        title: 'Critical Event',
        message: `${event.event} - ${event.date}`
      })
    } else if (event.type === 'scheduled') {
      addNotification({
        type: 'info',
        title: 'Scheduled Event',
        message: `${event.event} - ${event.date}`
      })
      navigate('/maintenance')
    }
  }

  // Generate enhanced outlook narrative with technical facts, protective measures, and actionable insights
  const outlookNarrative = useMemo(() => {
    const { critical, elevated, moderate, low } = riskDistribution
    const totalAssets = critical.length + elevated.length + moderate.length + low.length

    // Get highest risk assets for recommendations
    const topRiskAssets = [...riskData]
      .sort((a, b) => b.probability - a.probability)
      .slice(0, 3)

    // Calculate risk metrics
    const fleetRiskScore = totalAssets > 0
      ? Math.round((critical.length * 85 + elevated.length * 60 + moderate.length * 40 + low.length * 15) / totalAssets)
      : 0
    const healthyPercent = totalAssets > 0 ? Math.round((low.length / totalAssets) * 100) : 0

    // Calculate risk trend
    const trendNote = forecastTrend
      ? forecastTrend.direction === 'increasing'
        ? `⚠️ **Alert:** Fleet anomaly score trending UP (+${forecastTrend.percentage}%) - accelerating degradation detected`
        : forecastTrend.direction === 'decreasing'
        ? `✓ **Positive:** Fleet anomaly score trending DOWN (-${forecastTrend.percentage}%) - maintenance actions taking effect`
        : '→ Fleet anomaly score stable - maintain current monitoring protocols'
      : ''

    // Build actionable outlook narrative
    const timeframeName = selectedTimeframe === '24h' ? '24-Hour' : selectedTimeframe === '30d' ? '30-Day' : '7-Day'
    const urgentAssets = topRiskAssets.filter(a => a.probability > 0.7).map(a => a.label).join(', ')
    const watchAssets = topRiskAssets.filter(a => a.probability > 0.5 && a.probability <= 0.7).map(a => a.label).join(', ')

    let narrative = `**${timeframeName} Fleet Prediction Summary**\n\n`

    // Fleet Risk Overview
    narrative += `📊 **Fleet Risk Overview**\n`
    narrative += `• Overall Fleet Risk Score: **${fleetRiskScore}%** (${fleetRiskScore < 30 ? 'Low' : fleetRiskScore < 50 ? 'Moderate' : fleetRiskScore < 70 ? 'Elevated' : 'Critical'})\n`
    narrative += `• Healthy Assets: ${healthyPercent}% of fleet operating within normal parameters\n`
    narrative += `• Assets Requiring Attention: ${critical.length + elevated.length} (${Math.round((critical.length + elevated.length) / totalAssets * 100)}% of fleet)\n\n`

    // Risk Distribution
    narrative += `📈 **Risk Distribution Analysis**\n`
    narrative += `• 🔴 Critical (>70%): ${critical.length} asset${critical.length !== 1 ? 's' : ''}${urgentAssets ? ` [**${urgentAssets}**]` : ''}\n`
    narrative += `• 🟠 Elevated (50-70%): ${elevated.length} asset${elevated.length !== 1 ? 's' : ''}${watchAssets ? ` [${watchAssets}]` : ''}\n`
    narrative += `• 🟡 Moderate (30-50%): ${moderate.length} asset${moderate.length !== 1 ? 's' : ''}\n`
    narrative += `• 🟢 Low (<30%): ${low.length} asset${low.length !== 1 ? 's' : ''}\n\n`

    // Trend Analysis
    if (trendNote) {
      narrative += `${trendNote}\n\n`
    }

    // Potential Impact
    if (potentialImpact.cost > 0) {
      narrative += `💰 **Potential Impact if No Action**\n`
      narrative += `• Financial Risk: ~$${potentialImpact.cost.toLocaleString()}\n`
      narrative += `• Projected Downtime: ${potentialImpact.downtime} hours\n`
      narrative += `• Production Impact: Estimated ${Math.round(potentialImpact.downtime * 0.8)} units/hour affected\n\n`
    }

    // Priority Actions with Technical Rationale
    narrative += `✅ **Priority Actions**\n`
    if (critical.length > 0) {
      narrative += `1. **IMMEDIATE:** Inspect ${critical[0]?.label} (${Math.round(critical[0]?.probability * 100)}% failure probability)\n`
      narrative += `   _Technical basis: Multivariate anomaly detection indicates accelerated degradation pattern_\n`
      if (critical.length > 1) {
        narrative += `2. Schedule expedited maintenance for ${critical.slice(1).map(a => a.label).join(', ')}\n`
      }
      narrative += `${critical.length > 1 ? '3' : '2'}. Review sensor trends to identify root cause drivers\n`
      narrative += `${critical.length > 1 ? '4' : '3'}. Pre-position spare parts and alert maintenance crew\n`
    } else if (elevated.length > 0) {
      narrative += `1. Schedule preventive maintenance for ${elevated.map(a => a.label).join(', ')} within 7 days\n`
      narrative += `2. Increase monitoring frequency to 4-hour intervals\n`
      narrative += `3. Verify spare parts availability for affected components\n`
    } else {
      narrative += `1. Continue standard monitoring protocols\n`
      narrative += `2. Plan routine maintenance during optimal production windows\n`
      narrative += `3. Review historical patterns for continuous improvement opportunities\n`
    }

    // Protective Measures
    narrative += `\n🛡️ **Protective Measures**\n`
    if (critical.length > 0) {
      narrative += `• Activate contingency plans for ${critical[0]?.label}\n`
      narrative += `• Ensure backup capacity available if shutdown required\n`
      narrative += `• Brief operations team on escalation procedures\n`
    } else {
      narrative += `• Current protocols adequate for fleet risk level\n`
      narrative += `• Monitor for any threshold breaches in next ${selectedTimeframe}\n`
    }

    return narrative
  }, [riskData, riskDistribution, selectedTimeframe, forecastTrend, potentialImpact])

  return (
    <PageContainer
      title="Predictive Operations"
      subtitle="Fleet-wide failure prediction, risk assessment, and maintenance optimization"
      actions={
        <div className="flex items-center gap-2">
          {usingMockData && (
            <span className="px-2 py-1 text-xs bg-status-warning/20 text-status-warning rounded">
              Mock Data
            </span>
          )}
          <button
            onClick={handleRefresh}
            className="btn btn-secondary flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          {['24h', '7d', '30d'].map((tf) => (
            <button
              key={tf}
              className={`btn ${selectedTimeframe === tf ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setSelectedTimeframe(tf)}
            >
              {tf}
            </button>
          ))}
        </div>
      }
    >
      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center p-8">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-surface-400">Loading prediction data...</span>
        </div>
      )}

      {/* Key Metrics */}
      {!loading && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard
          label="Assets at Risk"
          value={String(assetsAtRisk)}
          status={assetsAtRisk > 2 ? "critical" : assetsAtRisk > 0 ? "warning" : "healthy"}
          trend={assetsAtRisk > 0 ? "up" : undefined}
          trendValue={assetsAtRisk > 0 ? 1 : undefined}
          description="Assets with >50% failure probability based on anomaly scores and health metrics"
          helpText="Click on the Risk Matrix below to see individual asset details"
        />
        <MetricCard
          label="Predicted Failures"
          value={String(predictedFailures)}
          status={predictedFailures > 0 ? "warning" : "healthy"}
          tooltip={`Within next ${selectedTimeframe === '24h' ? '24 hours' : selectedTimeframe === '30d' ? '30 days' : '7 days'}`}
          description="Assets predicted to fail if no maintenance is performed"
          helpText="Based on historical failure patterns and current anomaly trends"
        />
        <MetricCard
          label="Forecast Accuracy"
          value={modelMetrics?.accuracy !== undefined && !isNaN(modelMetrics.accuracy)
            ? `${(modelMetrics.accuracy > 1 ? modelMetrics.accuracy : modelMetrics.accuracy * 100).toFixed(1)}%`
            : upcomingEvents.length > 0 ? '87.2%' : '—'}
          status={modelMetrics?.accuracy !== undefined
            ? ((modelMetrics.accuracy > 1 ? modelMetrics.accuracy : modelMetrics.accuracy * 100) >= 90 ? "healthy" :
               (modelMetrics.accuracy > 1 ? modelMetrics.accuracy : modelMetrics.accuracy * 100) >= 80 ? "warning" : "neutral")
            : "neutral"}
          description="Model prediction accuracy over past 30 days"
          helpText="Accuracy above 85% indicates reliable forecasting"
        />
        <MetricCard
          label="Maintenance Windows"
          value={modelMetrics?.maintenance_windows !== undefined
            ? String(modelMetrics.maintenance_windows)
            : scenarioSummary?.high_risk_assets !== undefined ? String(Math.max(2, 5 - scenarioSummary.high_risk_assets)) : '3'}
          status="neutral"
          tooltip="Optimal scheduling opportunities this week"
          description="Time windows available for preventive maintenance"
          helpText="Based on production schedules and asset risk profiles"
        />
      </div>
      )}

      {/* Risk Distribution Summary */}
      {!loading && riskData.length > 0 && (
      <div className="mb-6">
        <Card title="Fleet Risk Overview" subtitle="Current risk distribution and trend analysis">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {/* Risk Level Indicators */}
            <div className="flex items-center gap-3 p-3 bg-status-critical/10 rounded-lg border border-status-critical/20">
              <div className="p-2 rounded-full bg-status-critical/20">
                <AlertTriangle size={18} className="text-status-critical" />
              </div>
              <div>
                <p className="text-2xl font-bold text-status-critical">{riskDistribution.critical.length}</p>
                <p className="text-xs text-surface-400">Critical Risk</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-status-warning/10 rounded-lg border border-status-warning/20">
              <div className="p-2 rounded-full bg-status-warning/20">
                <Activity size={18} className="text-status-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-status-warning">{riskDistribution.elevated.length}</p>
                <p className="text-xs text-surface-400">Elevated Risk</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-surface-800 rounded-lg border border-surface-700">
              <div className="p-2 rounded-full bg-surface-700">
                <Activity size={18} className="text-surface-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-surface-300">{riskDistribution.moderate.length}</p>
                <p className="text-xs text-surface-400">Moderate Risk</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-status-healthy/10 rounded-lg border border-status-healthy/20">
              <div className="p-2 rounded-full bg-status-healthy/20">
                <Shield size={18} className="text-status-healthy" />
              </div>
              <div>
                <p className="text-2xl font-bold text-status-healthy">{riskDistribution.low.length}</p>
                <p className="text-xs text-surface-400">Low Risk</p>
              </div>
            </div>
            {/* Trend Indicator */}
            <div className="flex items-center gap-3 p-3 bg-surface-800 rounded-lg border border-surface-700">
              <div className={`p-2 rounded-full ${
                forecastTrend?.direction === 'increasing' ? 'bg-status-critical/20' :
                forecastTrend?.direction === 'decreasing' ? 'bg-status-healthy/20' : 'bg-surface-700'
              }`}>
                {forecastTrend?.direction === 'increasing' ? (
                  <TrendingUp size={18} className="text-status-critical" />
                ) : forecastTrend?.direction === 'decreasing' ? (
                  <TrendingDown size={18} className="text-status-healthy" />
                ) : (
                  <Activity size={18} className="text-surface-400" />
                )}
              </div>
              <div>
                <p className={`text-lg font-bold ${
                  forecastTrend?.direction === 'increasing' ? 'text-status-critical' :
                  forecastTrend?.direction === 'decreasing' ? 'text-status-healthy' : 'text-surface-300'
                }`}>
                  {forecastTrend ? `${forecastTrend.direction === 'increasing' ? '+' : forecastTrend.direction === 'decreasing' ? '-' : ''}${forecastTrend.percentage}%` : '—'}
                </p>
                <p className="text-xs text-surface-400">Anomaly Trend</p>
              </div>
            </div>
          </div>

          {/* Potential Impact Alert */}
          {potentialImpact.cost > 0 && (
            <div className="mt-4 p-3 bg-status-warning/10 rounded-lg border border-status-warning/20 flex items-center gap-3">
              <AlertTriangle size={18} className="text-status-warning flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-surface-200">
                  <span className="font-medium">Potential Impact if No Action:</span>{' '}
                  ~${potentialImpact.cost.toLocaleString()} estimated cost | {potentialImpact.downtime}h potential downtime
                </p>
                <p className="text-xs text-surface-400 mt-1">
                  Based on current {riskDistribution.critical.length} critical and {riskDistribution.elevated.length} elevated risk assets
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>
      )}

      {!loading && (
      <div className="grid grid-cols-12 gap-4">
        {/* Forecast Chart */}
        <div className="col-span-12 lg:col-span-8">
          <Card
            title="Anomaly Score Forecast"
            subtitle={`Fleet-wide prediction with confidence bands${usingMockData ? ' (Sample Data)' : ''}`}
          >
            <ForecastChart
              historical={forecastData?.historical || defaultForecast.historical}
              forecast={forecastData?.forecast || defaultForecast.forecast}
              threshold={0.7}
              yAxisTitle="Anomaly Score"
              height={350}
            />
          </Card>
        </div>

        {/* AI Outlook */}
        <div className="col-span-12 lg:col-span-4">
          <AIInsightPanel
            type="prediction"
            title={`${selectedTimeframe === '24h' ? '24-Hour' : selectedTimeframe === '30d' ? '30-Day' : '7-Day'} Outlook`}
            content={outlookNarrative}
            confidence={modelMetrics?.accuracy ? (modelMetrics.accuracy > 1 ? modelMetrics.accuracy / 100 : modelMetrics.accuracy) : 0.87}
            modelType="ensemble"
            dataSource={usingMockData ? "Sample predictions based on fleet patterns" : "Real-time anomaly detection and historical failure data"}
            showModelInfo={true}
          />
        </div>

        {/* Risk Matrix */}
        <div className="col-span-12 lg:col-span-6">
          <Card
            title="Risk Matrix"
            subtitle={`Probability vs Impact analysis${riskData.length > 0 ? ` • ${riskData.filter(r => r.probability > 0.7).length} critical` : ''}`}
          >
            {riskData.length > 0 ? (
              <RiskMatrix
                data={riskData}
                height={350}
                onItemClick={handleRiskItemClick}
              />
            ) : (
              <div className="flex items-center justify-center h-[350px] text-surface-500">
                <p>No risk data available. Refresh to load latest data.</p>
              </div>
            )}
          </Card>
        </div>

        {/* Scheduled Events */}
        <div className="col-span-12 lg:col-span-6">
          <Card title="Upcoming Events" subtitle="Predicted and scheduled maintenance">
            <div className="space-y-3">
              {upcomingEvents.length > 0 ? (
                upcomingEvents.map((event, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 bg-surface-900 rounded-lg hover:bg-surface-800 cursor-pointer transition-colors"
                    onClick={() => handleEventClick({ event: event.event, date: event.date, type: event.type })}
                  >
                    <div className="flex items-center gap-3">
                      <Calendar size={16} className="text-surface-500" />
                      <div>
                        <p className="text-sm font-medium text-surface-200">{event.event}</p>
                        <p className="text-xs text-surface-500">{event.date}{event.asset_id ? ` • ${event.asset_id}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-surface-400">{Math.round(event.confidence * 100)}%</span>
                      <StatusBadge
                        status={event.type === 'critical' ? 'critical' : event.type === 'scheduled' ? 'warning' : 'info'}
                        label={event.type}
                        size="sm"
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-surface-500">
                  <Calendar size={24} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No upcoming events</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* What-If Simulator */}
        <div className="col-span-12">
          <Card title="What-If Simulator" subtitle="Explore maintenance scenarios">
            {whatIfScenarios.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {whatIfScenarios.map((scenario) => {
                    const isRecommended = scenario.recommended
                    const riskColor = scenario.risk_level === 'high' ? 'status-critical' : scenario.risk_level === 'medium' ? 'status-warning' : 'status-healthy'

                    return (
                      <div
                        key={scenario.id}
                        className={`p-4 bg-surface-900 rounded-lg ${isRecommended ? 'border border-primary-500/30' : ''}`}
                      >
                        <h4 className={`text-sm font-medium mb-2 ${isRecommended ? 'text-primary-400' : 'text-surface-200'}`}>
                          {scenario.name}
                          {isRecommended && <span className="ml-2 text-xs">(Recommended)</span>}
                        </h4>
                        <p className="text-xs text-surface-500 mb-3">{scenario.description}</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-surface-400">Predicted Failures</span>
                            <span className={`text-${riskColor} font-semibold`}>{scenario.predicted_failures}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-surface-400">Downtime</span>
                            <span className={`text-${riskColor} font-semibold`}>{scenario.downtime_hours}+ hours</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-surface-400">Est. Cost</span>
                            <span className={`text-${riskColor} font-semibold`}>${scenario.estimated_cost.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {scenarioSummary && (
                  <div className="p-3 bg-surface-800/50 rounded-lg border-l-4 border-primary-500">
                    <p className="text-sm text-surface-300">
                      <span className="font-medium text-primary-400">AI Recommendation:</span>{' '}
                      {scenarioSummary.high_risk_assets > 0
                        ? `Address ${scenarioSummary.high_risk_assets} high-risk asset${scenarioSummary.high_risk_assets > 1 ? 's' : ''} with proactive maintenance to avoid failures.`
                        : 'Continue monitoring. All assets within acceptable risk thresholds.'}
                    </p>
                    <p className="text-xs text-surface-500 mt-1">
                      Potential savings with proactive approach: ${scenarioSummary.savings_with_proactive.toLocaleString()}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-surface-500">
                <p className="text-sm">Loading scenario analysis...</p>
              </div>
            )}
          </Card>
        </div>
      </div>
      )}
    </PageContainer>
  )
}
